import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth, fetchOptionalAuth } from '../services/AuthService';
import { useAuth } from '../services/AuthContext';
import PieQuizComponent from './PieQuizComponent';
import { useTranslation } from 'react-i18next';

import '../css/BodyScan.css';

const COLORS = ['#6F3F89', '#375A9C', '#029FB2', '#29A648', '#E0B614', '#DA5E20', '#C11D24'];

const BodyScan = () => {
  // Tableau des questions
  const { t } = useTranslation();
  const { isUserAuthenticated, authLoading } = useAuth();

  const QUESTIONS_COUNT = 7;

  const questions = Array.from({ length: QUESTIONS_COUNT }, (_, i) => ({
    text: t(`scan.question${i + 1}`),
    details: t(`scan.footer${i + 1}`),
  }));

  // État pour la question en cours et les réponses
  const [highlightIndex, setHighlightIndex] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState(Array(questions.length).fill(null)); // Valeurs par défaut null  
  
  const [durationTimer, setDurationTimer] = useState(0); // ⏱️ durée totale du quiz (en secondes)
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const isAnswerSelected = answers[currentQuestionIndex] !== null;

  const navigate = useNavigate();

    useEffect(() => {
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDurationTimer(elapsed);
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  // Fonction pour changer de question (avancer ou revenir)
  const navigateBtn = (direction) => {
    const newIndex = currentQuestionIndex + direction;
    if (newIndex >= 0 && newIndex < questions.length) {
      setCurrentQuestionIndex(newIndex);
    }
  };

  const calculateScoring = (answers) => {
    return answers.reduce((total, x) => total + (2 * x - 10), 0);
  };

  const handleAnswerClick = (value) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = value;
    setAnswers(newAnswers);

     if (!hasStarted) {
      setHasStarted(true);
      logStartScan();
    }
  };

  const handleSubmitClick = async () => {
    clearInterval(timerRef.current);
    const scoringValue = answers ? calculateScoring(answers) : 0;
    const duration = durationTimer;

    if (authLoading) return;
    
    // Si pas connecté → stocker localement
    if (!isUserAuthenticated) {
      localStorage.setItem('answers', JSON.stringify(answers));
      localStorage.setItem('scoringValue', scoringValue);
      localStorage.setItem('quizDuration', duration);

      navigate('/result', { state: { answers, scoringValue, duration } });
      return;
    }

    //setIsSaving(true);

    let data;
    try {
      const response = await fetchWithAuth('/api/results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers, scoring: scoringValue, duration: durationTimer }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde des résultats.');
      }
      
      data = await response.json();
      //console.log("Data", data, data.result);
      //console.log('Résultats sauvegardés avec succès :', data);      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde :', error);
      alert('Une erreur est survenue. Réessayez.');
    } finally {
      //setIsSaving(false);
      // Redirigez vers la page des résultats en passant les données
      navigate('/Result', { state: { answers, scoringValue, scanId: data?.result._id } });
    }
  };
  
  useEffect(() => {
    let interval = null;
    let timeout = null;
    let i = 0;

    setHighlightIndex(null); // reset au changement de question

    const startChenillard = () => {
      i = 0;
      interval = setInterval(() => {
        setHighlightIndex(i);

        if (i === 9) { 
          // dernier bouton
          clearInterval(interval);
          interval = null;

          // l’éteindre après 150ms pour qu’on le voie allumé un instant
          setTimeout(() => setHighlightIndex(null), 150);

          // attendre 10s avant de relancer si pas de réponse
          timeout = setTimeout(() => {
            if (answers[currentQuestionIndex] === null) {
              startChenillard();
            }
          }, 10000);
        }

        i++;
      }, 150); // vitesse du chenillard
    };

    if (answers[currentQuestionIndex] === null) {
      timeout = setTimeout(() => {
        startChenillard();
      }, 10000);
    }

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      setHighlightIndex(null);
    };
  }, [answers, currentQuestionIndex]);

  const logStartScan = async () => {
    try {
      const response = await fetchOptionalAuth('/api/results/startScan', {
        method: 'POST',
        body: "",
      });

      if (!response.ok) {
        throw new Error('Erreur lors du démarrage du scan');
      }

      await response.json();
    } catch (error) {
      console.error('Erreur log startScan:', error);
    }
  };

  const questionColor = COLORS[COLORS.length - currentQuestionIndex - 1];

  return (
    <div className="bodyscan">
      <PieQuizComponent currentQuestion={currentQuestionIndex + 1} />

      {/* Affichage de la question */}
      <div className="question">
        <p className="scale-intro">{t('scan.header')}</p>
        <p className="main-question">{currentQuestion.text}</p>
      </div>

      <div className="response-container">
      <div className="slider">
      {[...Array(10)].map((_, i) => {
        const value = i + 1;
        const isSelected = answers[currentQuestionIndex] === value;
        const isHighlight = highlightIndex === i;

        let buttonColor = isSelected ? questionColor : '#aaa';
        if (!isSelected && isHighlight) {
          buttonColor = questionColor;
        }

        return (
          <button
            key={value}
            className={`slider-button ${isSelected ? 'selected' : ''} ${isHighlight ? 'highlight' : ''}`}
            style={{ backgroundColor: buttonColor }}
            onClick={() => handleAnswerClick(value)}
          >
            {value}
          </button>
        );
      })}
    </div>

      <p className="answer-details">{currentQuestion.details}</p>

      <div className="navigation">
      <button
        onClick={() => navigateBtn(-1)}
        disabled={currentQuestionIndex === 0}
      >
        {t('scan.prev')}
      </button>

      {currentQuestionIndex < questions.length - 1 ? (
        <button onClick={() => navigateBtn(1)} disabled={!isAnswerSelected}>
          {t('scan.next')}
        </button>
      ) : (
        <button onClick={handleSubmitClick} disabled={!isAnswerSelected}>
          {t('scan.finish')}
        </button>
      )}
    </div>
    </div>
  </div>
);
};

export default BodyScan;